// The VFR fuel plan: trip fuel, a contingency on it, and a timed final
// reserve, against the fuel on board. Ported from webplan-modern's
// fuelPlan(), which ignored the aircraft's unusable fuel -- here it is taken
// off the fuel on board before anything is compared with it, since a gauge
// or a dipstick reads it too.
//
// All fuel in US gallons, burn in US gallons per hour
// (flight.aircraft.fuel_consumption_usg). Pure; see navigation.ts.

export const DEFAULT_RESERVE_MINUTES = 45
export const DEFAULT_CONTINGENCY = 0.1

export const LITRES_PER_USG = 3.785411784

export function litresToUsg(litres: number): number {
  return litres / LITRES_PER_USG
}

export function usgToLitres(usg: number): number {
  return usg * LITRES_PER_USG
}

export interface FuelPlanInput {
  burnUsgPerHour: number
  tripMinutes: number
  reserveMinutes?: number
  /** Fraction of trip fuel carried as contingency (0.1 = 10 %). */
  contingency?: number
  fuelOnBoardUsg: number
  unusableUsg?: number
}

export interface FuelPlan {
  tripUsg: number
  contingencyUsg: number
  reserveUsg: number
  /** Trip + contingency + final reserve: the usable fuel the flight needs. */
  requiredUsg: number
  usableUsg: number
  /** Usable fuel left on landing if the trip goes to plan. */
  onArrivalUsg: number
  /** Usable fuel on board minus what is required; negative means short. */
  marginUsg: number
  sufficient: boolean
  /** Minutes of flight the usable fuel gives at this burn (ICAO item 19 E/). */
  enduranceMinutes: number
}

function round1(n: number) {
  return Math.round(n * 10) / 10
}

export function fuelPlan(input: FuelPlanInput): FuelPlan {
  const burn = Math.max(input.burnUsgPerHour, 0)
  const trip = burn * (Math.max(input.tripMinutes, 0) / 60)
  const contingency = trip * (input.contingency ?? DEFAULT_CONTINGENCY)
  const reserve = burn * ((input.reserveMinutes ?? DEFAULT_RESERVE_MINUTES) / 60)
  const required = trip + contingency + reserve
  const usable = Math.max(input.fuelOnBoardUsg - (input.unusableUsg ?? 0), 0)
  return {
    tripUsg: round1(trip),
    contingencyUsg: round1(contingency),
    reserveUsg: round1(reserve),
    requiredUsg: round1(required),
    usableUsg: round1(usable),
    onArrivalUsg: round1(usable - trip),
    marginUsg: round1(usable - required),
    sufficient: usable >= required,
    enduranceMinutes: burn > 0 ? Math.floor((usable / burn) * 60) : 0,
  }
}
